import api from './api';

class AssignmentService {
  // Get all assignments with pagination and filtering
  static async getAssignments(params = {}) {
    try {
      const response = await api.get('/assignments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Trigger backend scheduler to check and update overdue assignments
  static async checkOverdue() {
    try {
      const response = await api.post('/assignments/check-overdue');
      return response.data;
    } catch (error) {
      console.error('Error checking overdue assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment by ID
  static async getAssignmentById(id) {
    try {
      const response = await api.get(`/assignments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment:', error);
      throw error.response?.data || error;
    }
  }

  // Create new assignment
  static async createAssignment(assignmentData) {
    try {
      const response = await api.post('/assignments', assignmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating assignment:', error);
      throw error.response?.data || error;
    }
  }

  // Update assignment
  static async updateAssignment(id, assignmentData) {
    try {
      const response = await api.put(`/assignments/${id}`, assignmentData);
      return response.data;
    } catch (error) {
      console.error('Error updating assignment:', error);
      throw error.response?.data || error;
    }
  }

  // Delete assignment (soft delete)
  static async deleteAssignment(id) {
    try {
      const response = await api.delete(`/assignments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting assignment:', error);
      throw error.response?.data || error;
    }
  }

  // Return asset assignment
  static async returnAssignment(id, returnData) {
    try {
      const response = await api.post(`/assignments/${id}/return`, returnData);
      return response.data;
    } catch (error) {
      console.error('Error returning assignment:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment statistics
  static async getAssignmentStats() {
    try {
      const response = await api.get('/assignments/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment statistics:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignments by user
  static async getAssignmentsByUser(userId, params = {}) {
    try {
      const response = await api.get(`/assignments/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching user assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignments by asset
  static async getAssignmentsByAsset(assetId, params = {}) {
    try {
      const response = await api.get(`/assignments/asset/${assetId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching asset assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment history
  static async getAssignmentHistory(assignmentId) {
    try {
      const response = await api.get(`/assignments/${assignmentId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment history:', error);
      throw error.response?.data || error;
    }
  }

  // Get utilization analytics
  static async getUtilizationAnalytics(timeRange = '30d') {
    try {
      const response = await api.get('/assignments/analytics/utilization', {
        params: { timeRange }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching utilization analytics:', error);
      throw error.response?.data || error;
    }
  }

  // Get department assignment stats
  static async getDepartmentStats() {
    try {
      const response = await api.get('/assignments/stats/departments');
      return response.data;
    } catch (error) {
      console.error('Error fetching department stats:', error);
      throw error.response?.data || error;
    }
  }

  // Get asset type assignment stats
  static async getAssetTypeStats() {
    try {
      const response = await api.get('/assignments/stats/asset-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching asset type stats:', error);
      throw error.response?.data || error;
    }
  }

  // Get overdue assignments
  static async getOverdueAssignments() {
    try {
      const response = await api.get('/assignments/overdue');
      return response.data;
    } catch (error) {
      console.error('Error fetching overdue assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Get upcoming returns
  static async getUpcomingReturns(days = 30) {
    try {
      const response = await api.get('/assignments/upcoming-returns', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming returns:', error);
      throw error.response?.data || error;
    }
  }

  // Export assignments data
  static async exportAssignments(format = 'csv', filters = {}) {
    try {
      const response = await api.get('/assignments/export', {
        params: { format, ...filters },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Bulk update assignments
  static async bulkUpdateAssignments(updates) {
    try {
      const response = await api.post('/assignments/bulk-update', updates);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating assignments:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment trends
  static async getAssignmentTrends(timeRange = '12m') {
    try {
      const response = await api.get('/assignments/trends', {
        params: { timeRange }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment trends:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment alerts
  static async getAssignmentAlerts() {
    try {
      const response = await api.get('/assignments/alerts');
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment alerts:', error);
      throw error.response?.data || error;
    }
  }

  // Mark alert as read
  static async markAlertAsRead(alertId) {
    try {
      const response = await api.put(`/assignments/alerts/${alertId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error.response?.data || error;
    }
  }

  // Get assignment history
  static async getAssignmentHistory(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.actionType) params.append('actionType', filters.actionType);
      if (filters.user) params.append('user', filters.user);
      if (filters.department) params.append('department', filters.department);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await api.get(`/assignments/history?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment history:', error);
      throw error.response?.data || error;
    }
  }
}

export default AssignmentService;
